import { z } from "zod";

const jsonObjectSchema = z.record(z.string(), z.unknown());
const dateTimeSchema = z.string().datetime({ offset: true });
const nullableDateTimeSchema = dateTimeSchema.nullable();

export const conversationExecutionNodeKindSchema = z.enum([
  "phase",
  "reasoning_summary",
  "tool",
  "retrieval",
  "execution",
  "approval",
  "result",
]);

export const conversationExecutionNodeStatusSchema = z.enum([
  "queued",
  "running",
  "waiting_input",
  "approval_required",
  "succeeded",
  "failed",
  "canceled",
]);

function isSafeAbsoluteHTTPURL(value: string) {
  try {
    const parsed = new URL(value);
    return (
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      parsed.hostname.length > 0 &&
      parsed.username === "" &&
      parsed.password === ""
    );
  } catch {
    return false;
  }
}

function isSafeSourceIconURL(value: string) {
  if (value.startsWith("/") && !value.startsWith("//")) return true;
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === "https:" &&
      parsed.hostname.length > 0 &&
      parsed.username === "" &&
      parsed.password === ""
    );
  } catch {
    return false;
  }
}

export const conversationExecutionSourceWireSchema = z
  .object({
    kind: z.enum(["cloud_resource", "document", "web", "tool", "database"]),
    label: z.string().trim().min(1).max(240),
    locator: z.string().trim().max(500).optional(),
    observed_at: dateTimeSchema.optional(),
    resource_id: z.string().trim().max(500).optional(),
    schema: z.string().trim().max(500).optional(),
    excerpt: z.string().trim().max(500).optional(),
    icon_url: z
      .string()
      .trim()
      .max(500)
      .refine(isSafeSourceIconURL, "来源图标地址必须是安全 HTTPS 或同源路径")
      .optional(),
  })
  .strict()
  .superRefine((source, context) => {
    if (
      source.kind === "web" &&
      (!source.locator || !isSafeAbsoluteHTTPURL(source.locator))
    ) {
      context.addIssue({
        code: "custom",
        message: "网页来源必须包含安全 HTTP(S) 地址",
        path: ["locator"],
      });
    }
  });

const conversationExecutionDetailBlockWireSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string() }).strict(),
  z.object({ type: z.literal("markdown"), text: z.string() }).strict(),
  z
    .object({
      type: z.literal("key_value"),
      items: z.array(
        z.object({ label: z.string(), value: z.string() }).strict(),
      ),
    })
    .strict(),
  z
    .object({
      type: z.literal("table"),
      columns: z.array(z.string()),
      rows: z.array(z.array(z.string())),
    })
    .strict(),
  z
    .object({
      type: z.literal("metric"),
      label: z.string(),
      unit: z.string().optional(),
      value: z.string(),
    })
    .strict(),
  z
    .object({
      type: z.literal("progress"),
      current: z.number(),
      label: z.string(),
      total: z.number(),
      unit: z.string().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("resource_ref"),
      label: z.string(),
      resource_id: z.string().min(1),
      version: z.union([z.string(), z.number()]).optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("receipt"),
      label: z.string(),
      status: z.string(),
      value: z.string().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("error"),
      code: z.string(),
      message: z.string(),
    })
    .strict(),
  z
    .object({
      type: z.literal("capability_readiness"),
      capability_key: z.string().min(1).max(160),
      state: z.enum(["unavailable", "degraded"]),
      public_error_code: z.string().regex(/^[a-z0-9][a-z0-9._:-]{0,127}$/u),
      retryable: z.boolean(),
      observed_at: dateTimeSchema.optional(),
      request_id: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/u).optional(),
      upstream_request_id: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/u).optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("capability_action"),
      action: z.enum(["connect_gmail", "open_shop_prerequisite", "open_products", "retry_probe"]),
      label: z.string().min(1).max(80),
      capability_key: z.string().min(1).max(160),
      shop_binding_id: z.string().max(160),
    })
    .strict(),
]);

export const conversationExecutionNodeWireSchema = z
  .object({
    assistant_message_id: z.string(),
    completed_at: nullableDateTimeSchema,
    created_at: dateTimeSchema.optional(),
    depth: z.number().int().min(0).max(12),
    detail_blocks: z.array(conversationExecutionDetailBlockWireSchema),
    duration_ms: z.number().int().nonnegative().nullable(),
    kind: conversationExecutionNodeKindSchema,
    node_id: z.string().min(1),
    parent_node_id: z.string().min(1).nullable(),
    revision: z.number().int().positive(),
    schema: z.literal("conversation_execution_node.v1"),
    sequence: z.number().int().positive(),
    sources: z.array(conversationExecutionSourceWireSchema),
    started_at: nullableDateTimeSchema,
    status: conversationExecutionNodeStatusSchema,
    summary: z.string(),
    title: z.string().min(1),
    turn_id: z.string().min(1),
    updated_at: dateTimeSchema.optional(),
  })
  .strict();

export function toConversationExecutionNode(
  input: z.infer<typeof conversationExecutionNodeWireSchema>,
) {
  return {
    assistantMessageId: input.assistant_message_id,
    completedAt: input.completed_at,
    createdAt: input.created_at,
    depth: input.depth,
    detailBlocks: input.detail_blocks,
    durationMs: input.duration_ms,
    kind: input.kind,
    nodeId: input.node_id,
    parentNodeId: input.parent_node_id,
    revision: input.revision,
    sequence: input.sequence,
    sources: input.sources.map((source) => ({
      ...(source.excerpt !== undefined ? { excerpt: source.excerpt } : {}),
      ...(source.icon_url !== undefined ? { iconUrl: source.icon_url } : {}),
      kind: source.kind,
      label: source.label,
      ...(source.locator !== undefined ? { locator: source.locator } : {}),
      ...(source.observed_at !== undefined
        ? { observedAt: source.observed_at }
        : {}),
      ...(source.resource_id !== undefined
        ? { resourceId: source.resource_id }
        : {}),
      ...(source.schema !== undefined ? { schema: source.schema } : {}),
    })),
    startedAt: input.started_at,
    status: input.status,
    summary: input.summary,
    title: input.title,
    turnId: input.turn_id,
    updatedAt: input.updated_at,
  };
}

export const conversationExecutionNodesResponseSchema = z
  .object({
    execution_nodes: z.array(conversationExecutionNodeWireSchema),
    schema: z.literal("conversation_execution_node.v1"),
  })
  .strict()
  .transform((response) => ({
    executionNodes: response.execution_nodes.map(toConversationExecutionNode),
    schema: response.schema,
  }));

export const conversationMessageRoleSchema = z.enum([
  "assistant",
  "system",
  "tool",
  "user",
]);

const conversationQuoteCommonShape = {
  conversation_id: z.string().min(1),
  end_offset: z.number().int().positive(),
  offset_unit: z.literal("utf16"),
  organization_id: z.string().min(1),
  source_content_hash: z.string().regex(/^[a-f0-9]{64}$/),
  source_message_id: z.string().min(1),
  source_sequence: z.number().int().positive(),
  start_offset: z.number().int().nonnegative(),
  text: z.string().min(1),
  text_hash: z.string().regex(/^[a-f0-9]{64}$/),
};

const conversationQuoteV1WireSchema = z
  .object({
    ...conversationQuoteCommonShape,
    schema: z.literal("conversation_quote.v1"),
  })
  .strict();

const conversationQuoteV2WireSchema = z
  .object({
    ...conversationQuoteCommonShape,
    schema: z.literal("conversation_quote.v2"),
    source_projection: z.literal("rendered_text.v1"),
    source_projection_hash: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict();

export const conversationQuoteWireSchema = z
  .discriminatedUnion("schema", [
    conversationQuoteV1WireSchema,
    conversationQuoteV2WireSchema,
  ])
  .refine((quote) => quote.end_offset > quote.start_offset, {
    message: "引用结束位置必须大于开始位置",
  });

// UIMessage 会随 AI SDK 增加新的 part 类型。边界只开放 part 的载荷，
// 页面只能通过本文件导出的投影函数读取可见文本，不能在组件里自行断言字段。
export const conversationUIMessageWireSchema = z
  .object({
    id: z.string().min(1),
    metadata: jsonObjectSchema.optional(),
    parts: z.array(z.unknown()),
    role: conversationMessageRoleSchema,
  })
  .passthrough();

const conversationWireSchema = z
  .object({
    created_at: dateTimeSchema,
    created_by_user_id: z.string().min(1),
    id: z.string().min(1),
    last_message_at: nullableDateTimeSchema,
    message_count: z.number().int().nonnegative(),
    messages: z.array(conversationUIMessageWireSchema),
    metadata: jsonObjectSchema,
    organization_id: z.string().min(1),
    shop_binding_id: z.string().min(1).nullish().transform((value) => value ?? null),
    status: z.enum(["active", "archived"]),
    title: z.string().min(1),
    trigger_last_event_id: z.string().nullable(),
    trigger_run_id: z.string().nullable(),
    trigger_session_id: z.string().nullable(),
    trigger_task_id: z.string(),
    updated_at: dateTimeSchema,
  })
  .strict();

function toConversation(input: z.infer<typeof conversationWireSchema>) {
  return {
    createdAt: input.created_at,
    createdByUserId: input.created_by_user_id,
    id: input.id,
    lastMessageAt: input.last_message_at,
    messageCount: input.message_count,
    messages: input.messages,
    metadata: input.metadata,
    organizationId: input.organization_id,
    shopBindingId: input.shop_binding_id,
    status: input.status,
    title: input.title,
    triggerLastEventId: input.trigger_last_event_id,
    triggerRunId: input.trigger_run_id,
    triggerSessionId: input.trigger_session_id,
    triggerTaskId: input.trigger_task_id,
    updatedAt: input.updated_at,
  };
}

export const conversationsResponseSchema = z
  .object({ conversations: z.array(conversationWireSchema) })
  .strict()
  .transform((response) => ({
    conversations: response.conversations.map(toConversation),
  }));

export const conversationResponseSchema = z
  .object({ conversation: conversationWireSchema })
  .strict()
  .transform((response) => ({
    conversation: toConversation(response.conversation),
  }));

const conversationMessageLogWireSchema = z
  .object({
    content_hash: z.string().regex(/^[a-f0-9]{64}$/),
    created_at: dateTimeSchema,
    id: z.string().min(1),
    message: conversationUIMessageWireSchema,
    resource_refs: z.array(jsonObjectSchema),
    role: conversationMessageRoleSchema,
    sequence: z.number().int().positive(),
    updated_at: dateTimeSchema,
  })
  .strict();

function toConversationMessage(
  input: z.infer<typeof conversationMessageLogWireSchema>,
) {
  return {
    contentHash: input.content_hash,
    createdAt: input.created_at,
    id: input.id,
    message: input.message,
    resourceRefs: input.resource_refs,
    role: input.role,
    sequence: input.sequence,
    updatedAt: input.updated_at,
  };
}

export const conversationMessagesResponseSchema = z
  .object({
    has_more: z.boolean(),
    messages: z.array(conversationMessageLogWireSchema),
    next_before_sequence: z.number().int().positive().nullable(),
    storage_mode: z.enum([
      "dual_write_shadow_read",
      "legacy",
      "legacy_read_only",
      "log_primary",
    ]),
  })
  .strict()
  .transform((response) => ({
    hasMore: response.has_more,
    messages: response.messages.map(toConversationMessage),
    nextBeforeSequence: response.next_before_sequence,
    storageMode: response.storage_mode,
  }));

const conversationTurnWireSchema = z
  .object({
    assistant_preview: z.string(),
    created_at: dateTimeSchema,
    end_sequence: z.number().int().positive(),
    latest_assistant_message_id: z.string().min(1).nullable(),
    start_sequence: z.number().int().positive(),
    status: z.enum([
      "completed",
      "failed",
      "pending",
      "stopped",
      "streaming",
    ]),
    turn_id: z.string().min(1),
    updated_at: dateTimeSchema,
    user_message_id: z.string().min(1),
    user_preview: z.string().min(1),
  })
  .strict()
  .refine((turn) => turn.end_sequence >= turn.start_sequence, {
    message: "轮次结束序号不能早于开始序号",
  });

function toConversationTurn(input: z.infer<typeof conversationTurnWireSchema>) {
  return {
    assistantPreview: input.assistant_preview,
    createdAt: input.created_at,
    endSequence: input.end_sequence,
    latestAssistantMessageId: input.latest_assistant_message_id,
    startSequence: input.start_sequence,
    status: input.status,
    turnId: input.turn_id,
    updatedAt: input.updated_at,
    userMessageId: input.user_message_id,
    userPreview: input.user_preview,
  };
}

export const conversationTurnsResponseSchema = z
  .object({
    has_more: z.boolean(),
    next_before_sequence: z.number().int().positive().nullable(),
    turns: z.array(conversationTurnWireSchema),
  })
  .strict()
  .transform((response) => ({
    hasMore: response.has_more,
    nextBeforeSequence: response.next_before_sequence,
    turns: response.turns.map(toConversationTurn),
  }));

export const conversationStartSessionResponseSchema = z
  .object({
    conversation: conversationWireSchema,
    isCached: z.boolean(),
    publicAccessToken: z.string().min(1),
    runId: z.string().min(1),
    sessionId: z.string().min(1),
  })
  .strict()
  .transform((response) => ({
    ...response,
    conversation: toConversation(response.conversation),
  }));

export const conversationAccessTokenResponseSchema = z
  .object({ publicAccessToken: z.string().min(1) })
  .strict();

export type Conversation = ReturnType<typeof toConversation>;
export type ConversationMessage = ReturnType<typeof toConversationMessage>;
export type ConversationTurn = ReturnType<typeof toConversationTurn>;
export type ConversationExecutionNode = ReturnType<
  typeof toConversationExecutionNode
>;
export type ConversationUIMessageWire = z.infer<
  typeof conversationUIMessageWireSchema
>;
export type ConversationQuoteWire = z.infer<typeof conversationQuoteWireSchema>;
